import { AI_ERRORS } from './aiErrors';
import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Practice-section error definitions.
 *
 * Maps backend codes returned by the practice API surface
 * (`GET /api/questions`, `POST /api/grade`) and client-side fallback codes
 * to user-friendly copy scoped to the *practice* flow. Mirrors the
 * `subscriptionErrors.ts` / `onboardingGradingErrors.ts` per-domain pattern
 * so practice copy stays independent of the onboarding/quick-win flow.
 *
 * Reuses the shared `AI_ERRORS` map for Claude grading failures so the
 * AI-side wording stays consistent across the onboarding, mock-interview,
 * and practice flows.
 */
export const PRACTICE_ERRORS: Record<string, ErrorDefinition> = {
  ...AI_ERRORS,

  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in apiError.code)
  // ---------------------------------------------------------
  AUTHENTICATION_ERROR: {
    title: 'Session expired',
    message: 'Your session has expired.',
    action: 'Please sign in again to continue.',
    retryable: false,
    category: 'authentication',
  },

  VALIDATION_ERROR: {
    title: 'Invalid answer',
    message: "We couldn't read that answer.",
    action: 'Please try recording or typing again.',
    retryable: true,
    category: 'validation',
  },

  NOT_FOUND: {
    title: 'Question unavailable',
    message:
      "We couldn't find this question. We've been notified and are looking into it.",
    action: 'Please try a different question.',
    retryable: false,
    category: 'server',
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down a moment',
    message: "You've sent answers a little too quickly.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message:
      "We had trouble grading that answer. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback codes (lowercase, used when apiError.code is missing)
  // ---------------------------------------------------------
  questions_load_failed: {
    title: "Couldn't load questions",
    message: 'We had trouble loading the practice questions.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  grading_failed: {
    title: "Couldn't grade your answer",
    message: 'We had trouble reaching our grader.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'Grading your answer requires an internet connection.',
    action: 'Please connect to the internet and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a practice-section error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getPracticeError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...PRACTICE_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = PRACTICE_ERRORS[code] ?? {
    title: "Couldn't grade your answer",
    message:
      "An unexpected error occurred. We've been notified and are looking into it.",
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}

/**
 * Practice-section transcription error definitions.
 *
 * Maps backend codes returned by `POST /api/transcribe` (Deepgram) to
 * practice-flavored copy. Lives alongside `PRACTICE_ERRORS` (instead of in
 * the audio domain) so the practice surface owns its full error vocabulary
 * — codes like `VALIDATION_ERROR` and `RATE_LIMIT_EXCEEDED` overlap with the
 * grader endpoint and need different wording per request type, so we keep
 * the two maps separate within the practice domain.
 */
export const PRACTICE_TRANSCRIBE_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in apiError.code)
  // ---------------------------------------------------------
  VALIDATION_ERROR: {
    title: 'No audio recorded',
    message:
      "We didn't receive any audio with that recording. Please try recording again.",
    action: 'Please try recording again.',
    retryable: true,
    category: 'validation',
  },

  TRANSCRIPTION_INVALID_AUDIO: {
    title: "Couldn't read your recording",
    message:
      'The recording looks corrupted or in an unsupported format. Please try recording again.',
    action: 'Please try recording again.',
    retryable: true,
    category: 'validation',
  },

  TRANSCRIPTION_PAYMENT_REQUIRED: {
    title: 'Voice transcription unavailable',
    message:
      "Our transcription service has a billing issue and we've been notified and are looking into it. Please type your answer for now.",
    action: 'Please type your answer for now.',
    retryable: false,
    category: 'server',
  },

  TRANSCRIPTION_EMPTY_RESULT: {
    title: "Didn't hear anything",
    message:
      "We couldn't pick up any speech in that recording. Please speak louder and try again.",
    action: 'Please speak louder and try again.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Speak louder and closer to the microphone',
      'Move to a quieter location',
      'Check that your microphone is working',
    ],
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down a moment',
    message:
      "You've recorded a little too quickly. Please wait a few seconds and try again.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  TRANSCRIPTION_RATE_LIMITED: {
    title: 'Slow down a moment',
    message:
      "You've recorded a little too quickly. Please wait a few seconds and try again.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  TRANSCRIPTION_AUTH_ERROR: {
    title: 'Service error',
    message:
      "We're having trouble with our transcription service and we've been notified and are looking into it. Please type your answer or try again later.",
    action: 'Please type your answer or try again later.',
    retryable: true,
    retryAfterSeconds: 60,
    category: 'server',
  },

  TRANSCRIPTION_SERVICE_ERROR: {
    title: 'Transcription failed',
    message:
      "Something went wrong while transcribing and we've been notified and are looking into it. Please try again in a moment.",
    action: 'Please try again in a moment.',
    retryable: true,
    category: 'server',
  },

  TRANSCRIPTION_UNAVAILABLE: {
    title: 'Voice transcription unavailable',
    message:
      'Voice transcription is temporarily unavailable. Please type your answer for now.',
    action: 'Please type your answer for now.',
    retryable: true,
    retryAfterSeconds: 30,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback code
  // ---------------------------------------------------------
  transcription_failed: {
    title: "Couldn't transcribe your recording",
    message:
      'We had trouble reaching our transcription service. Please check your connection and try again.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a practice transcription error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getPracticeTranscribeError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...PRACTICE_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = PRACTICE_TRANSCRIBE_ERRORS[code] ?? {
    title: "Couldn't transcribe your recording",
    message:
      "Something went wrong while transcribing your answer. We've been notified and are looking into it.",
    action: 'Please try again or type your answer.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}
